import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AddJobTitleModalProps {
  isOpen: boolean;
  onClose: () => void;
  departments: string[];
  grades: string[];
}

interface FormData {
  title: string;
  department: string;
  grade: string;
  description: string;
  requirements: string[];
  reportingTo: string;
  status: string;
}

interface Errors {
  title?: string;
  department?: string;
  grade?: string;
  description?: string;
  requirements?: string;
}

const AddJobTitleModal: React.FC<AddJobTitleModalProps> = ({ isOpen, onClose, departments, grades }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    title: '',
    department: '',
    grade: '',
    description: '',
    requirements: [''],
    reportingTo: '',
    status: 'Active'
  });

  const [errors, setErrors] = useState<Errors>({});

  if (!isOpen) return null;

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field as keyof Errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleRequirementChange = (index: number, value: string) => {
    const newRequirements = [...formData?.requirements];
    newRequirements[index] = value;
    setFormData(prev => ({
      ...prev,
      requirements: newRequirements
    }));
  };

  const addRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev?.requirements, '']
    }));
  };

  const removeRequirement = (index: number) => {
    if (formData?.requirements?.length > 1) {
      const newRequirements = formData?.requirements?.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        requirements: newRequirements
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Errors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = t('Job title is required');
    }

    if (!formData?.department) {
      newErrors.department = t('Department is required');
    }

    if (!formData?.grade) {
      newErrors.grade = t('Grade is required');
    }

    if (!formData?.description?.trim()) {
      newErrors.description = t('Description is required');
    }

    if (formData?.requirements?.some(req => !req?.trim())) {
      newErrors.requirements = t('All requirement fields must be filled');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e?.preventDefault();

    if (validateForm()) {
      // Here you would typically make an API call to save the job title
      console.log('Creating job title:', formData);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">{t('Add New Job Title')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">{t('Basic Information')}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('Job Title')} *
                </label>
                <input
                  type="text"
                  value={formData?.title}
                  onChange={(e) => handleInputChange('title', e?.target?.value)}
                  placeholder={t('Enter job title')}
                  className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 ${
                    errors?.title ? 'border-red-500' : 'border-border'
                  }`}
                />
                {errors?.title && (
                  <p className="mt-1 text-sm text-red-600">{errors?.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('Department')} *
                </label>
                <select
                  value={formData?.department}
                  onChange={(e) => handleInputChange('department', e?.target?.value)}
                  className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 ${
                    errors?.department ? 'border-red-500' : 'border-border'
                  }`}
                >
                  <option value="">{t('Select Department')}</option>
                  {departments?.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
                {errors?.department && (
                  <p className="mt-1 text-sm text-red-600">{errors?.department}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('Grade')} *
                </label>
                <select
                  value={formData?.grade}
                  onChange={(e) => handleInputChange('grade', e?.target?.value)}
                  className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 ${
                    errors?.grade ? 'border-red-500' : 'border-border'
                  }`}
                >
                  <option value="">{t('Select Grade')}</option>
                  {grades?.map((grade) => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
                {errors?.grade && (
                  <p className="mt-1 text-sm text-red-600">{errors?.grade}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('Reports To')}
                </label>
                <input
                  type="text"
                  value={formData?.reportingTo}
                  onChange={(e) => handleInputChange('reportingTo', e?.target?.value)}
                  placeholder={t('Enter reporting manager title')}
                  className="w-full px-3 py-2 border border-border rounded-lg shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('Status')}
              </label>
              <select
                value={formData?.status}
                onChange={(e) => handleInputChange('status', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200"
              >
                <option value="Active">{t('Active')}</option>
                <option value="Draft">{t('Draft')}</option>
                <option value="Inactive">{t('Inactive')}</option>
              </select>
            </div>
          </div>

          {/* Job Description */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">{t('Job Description')}</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('Description')} *
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                placeholder={t('Enter detailed job description')}
                rows={4}
                className={`block w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 ${
                  errors?.description ? 'border-red-500' : 'border-border'
                }`}
              />
              {errors?.description && (
                <p className="mt-1 text-sm text-red-600">{errors?.description}</p>
              )}
            </div>
          </div>

          {/* Requirements */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{t('Requirements')}</h3>
              <button
                type="button"
                onClick={addRequirement}
                className="inline-flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                {t('Add Requirement')}
              </button>
            </div>

            <div className="space-y-3">
              {formData?.requirements?.map((requirement, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={requirement}
                    onChange={(e) => handleRequirementChange(index, e?.target?.value)}
                    placeholder={t('Enter job requirement')}
                    className="flex-1 px-3 py-2 border border-border rounded-lg shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200"
                  />
                  {formData?.requirements?.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRequirement(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {errors?.requirements && (
              <p className="text-sm text-red-600">{errors?.requirements}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg
                       hover:bg-primary/90 transition-colors duration-200"
            >
              {t('Create Job Title')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddJobTitleModal;
